import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/apiService';
import FlashMessage from './FlashMessage';
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
} from '../styles/SubscriptionFormModalStyles';
import {
  BillsTableWrapper,
  BillsTableEl,
  BillsTableHeader,
  BillsTableRow,
  BillsTableData,
  StatusBadgeBills,
} from '../styles/BillsTableStyles';
import { FormRow, Label, SubmitButton } from '../styles/ComponentStyles';
import { BillNumberInput, BillDateInput, HelperText } from '../styles/BillsFormStyles';
import { formatMoney } from '../utils/investmentMath';
import { sortByDate } from '../utils/sortByDate';
import { errorLines } from '../utils/apiError';
import {
  getPayableStatus,
  earlyLateLabel,
  STATUS_COLORS,
} from '../utils/billStatusHelper';

// Payables = a bill's due cycles. ONE payable is settled by at most ONE payment,
// and the server derives everything interesting from that pairing (how early or
// late the payment was, the charge — the linked payment's payment_charge,
// reported unconditionally whether the payment was early, on time or late — and
// the penalty, which is that same number, surfaced only when it was late).
// Charge and penalty are therefore one amount seen twice, never two amounts to
// add up. Nothing is recomputed here; this modal only reads what the endpoint
// already computed.
//
// No window.confirm/alert/prompt anywhere: deletes and unlinks use the same
// inline Confirm/Cancel swap as the rest of the app.

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({ due_date: today(), amount: '', remarks: '' });
const emptyGenerateForm = () => ({ start_date: today(), months: '12', amount: '' });

const textInputStyle = {
  width: '100%',
  maxWidth: 400,
  padding: '10px 12px',
  borderRadius: 6,
  border: '1px solid #555',
  backgroundColor: '#2e2e2e',
  color: '#e0e0e0',
};

const selectStyle = {
  minWidth: 190,
  maxWidth: 260,
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid #555',
  backgroundColor: '#2e2e2e',
  color: '#e0e0e0',
};

const linkBtnStyle = (color) => ({
  background: 'none',
  border: 'none',
  color,
  cursor: 'pointer',
  padding: '0 6px',
});

const buttonStyle = (background) => ({
  padding: '8px 16px',
  background,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
});

const panelStyle = {
  marginTop: 16,
  padding: '12px 14px',
  borderRadius: 6,
  border: '1px solid #4a7c9e',
  backgroundColor: '#22303a',
};

// FastAPI reports the reason in `detail` (a string for 4xx, an array for 422).
// A 409 from these endpoints is always an invariant violation — a payable that
// already has a payment, a payment that already settles another cycle, or a
// delete of a settled cycle — so it gets its own fallback wording when the body
// carries nothing readable.
const readError = (err, fallback, conflictFallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) return errorLines(detail, fallback).join(' ');
  if (err?.response?.status === 409 && conflictFallback) return conflictFallback;
  return err?.message || fallback;
};

// The bill payments GET Title-Cases its top-level keys; a payment read anywhere
// else is snake_case. Read both so the picker works either way.
const paymentId = (p) => p?.Id ?? p?.id ?? '';
const paymentDate = (p) => p?.['Payment Date'] ?? p?.payment_date ?? '';
const paymentAmount = (p) => p?.['Payment Amount'] ?? p?.payment_amount ?? '';
const paymentMethod = (p) => p?.['Payment Method'] ?? p?.payment_method ?? '';

// Payment dates can arrive as a plain "YYYY-MM-DD" or a full timestamp.
const dateOnly = (value) => {
  if (!value) return '';
  const text = String(value);
  const iso = text.match(/^\d{4}-\d{2}-\d{2}/);
  if (iso) return iso[0];
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};

const paymentLabel = (p) => {
  const parts = [dateOnly(paymentDate(p)) || 'undated', formatMoney(paymentAmount(p))];
  const method = paymentMethod(p);
  if (method) parts.push(method);
  return parts.join(' · ');
};

const BillPayablesModal = ({ bill, onClose, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flashMessage, setFlashMessage] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const [showGenerate, setShowGenerate] = useState(false);
  const [generateForm, setGenerateForm] = useState(emptyGenerateForm());
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState('');

  // payableId -> the payment chosen in that row's picker, plus which row is
  // mid-request and which row is showing the unlink confirm.
  const [linkChoice, setLinkChoice] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [pendingUnlinkId, setPendingUnlinkId] = useState(null);

  const billId = bill?._id;

  const setFlash = (msg) => {
    setFlashMessage(msg);
    setTimeout(() => setFlashMessage(''), 4000);
  };

  const fetchPayables = async () => {
    if (!billId) return;
    setLoading(true);
    try {
      const result = await apiService.getBillPayables(billId);
      setItems(result.items);
      setSummary(result.summary);
    } catch (err) {
      console.error(err);
      setItems([]);
      setSummary({});
      setFlash(readError(err, 'Could not load payables.'));
    } finally {
      setLoading(false);
    }
  };

  // The link picker offers this bill's own payments; failing to load them only
  // costs the picker, so it never blocks the table.
  const fetchPayments = async () => {
    if (!bill) return;
    try {
      const list = await apiService.getBillPayments(bill);
      setPayments(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      setPayments([]);
    }
  };

  useEffect(() => {
    fetchPayables();
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId]);

  const sortedItems = useMemo(
    () => sortByDate(items, 'due_date', sortDir),
    [items, sortDir]
  );

  // A payment settles at most one cycle, so anything already spoken for is kept
  // out of the other rows' pickers — the backend answers 409 for those.
  const linkedPaymentIds = useMemo(
    () => new Set(items.map((p) => p?.payment_id).filter(Boolean)),
    [items]
  );

  const availablePayments = (payable) =>
    payments.filter((p) => {
      const id = paymentId(p);
      if (!id) return false;
      return !linkedPaymentIds.has(id) || id === payable?.payment_id;
    });

  const refreshAll = async () => {
    await fetchPayables();
    await fetchPayments();
    if (onRefresh) onRefresh();
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerateChange = (e) => {
    const { name, value } = e.target;
    setGenerateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (payable) => {
    setEditingId(payable.id);
    setPendingDeleteId(null);
    setPendingUnlinkId(null);
    setForm({
      due_date: payable.due_date || today(),
      amount: payable.amount ?? '',
      remarks: payable.remarks ?? '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.due_date) {
      setFlash('Please pick a due date.');
      return;
    }
    const payload = {
      due_date: form.due_date,
      amount: form.amount === '' ? null : parseFloat(form.amount),
      remarks: form.remarks || null,
    };
    setSaving(true);
    try {
      if (editingId) {
        await apiService.updateBillPayable(billId, editingId, payload);
        setFlash('Payable updated.');
      } else {
        await apiService.addBillPayable(billId, payload);
        setFlash('Payable added.');
      }
      resetForm();
      await refreshAll();
    } catch (err) {
      setFlash(readError(err, 'Failed to save payable.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (payableId) => {
    setBusyId(payableId);
    try {
      await apiService.deleteBillPayable(billId, payableId);
      setFlash('Payable deleted.');
      setPendingDeleteId(null);
      if (editingId === payableId) resetForm();
      await refreshAll();
    } catch (err) {
      setFlash(
        readError(
          err,
          'Failed to delete payable.',
          'This cycle still has a linked payment. Unlink it first, then delete.'
        )
      );
      setPendingDeleteId(null);
    } finally {
      setBusyId(null);
    }
  };

  const handleLink = async (payableId) => {
    const chosen = linkChoice[payableId];
    if (!chosen) {
      setFlash('Pick a payment to link first.');
      return;
    }
    setBusyId(payableId);
    try {
      await apiService.linkPayablePayment(billId, payableId, chosen);
      setFlash('Payment linked to this cycle.');
      setLinkChoice((prev) => ({ ...prev, [payableId]: '' }));
      await refreshAll();
    } catch (err) {
      setFlash(
        readError(
          err,
          'Failed to link payment.',
          'That payment or that cycle is already linked.'
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleUnlink = async (payableId) => {
    setBusyId(payableId);
    try {
      await apiService.unlinkPayablePayment(billId, payableId);
      setFlash('Payment unlinked. The payment itself was not touched.');
      setPendingUnlinkId(null);
      await refreshAll();
    } catch (err) {
      setFlash(readError(err, 'Failed to unlink payment.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    const months = parseInt(generateForm.months, 10);
    if (!generateForm.start_date) {
      setFlash('Please pick a start date.');
      return;
    }
    if (!Number.isInteger(months) || months < 1 || months > 120) {
      setFlash('Number of months must be between 1 and 120.');
      return;
    }
    setGenerating(true);
    setGenerateResult('');
    try {
      const result = await apiService.generateBillPayables(billId, {
        start_date: generateForm.start_date,
        months,
        amount: generateForm.amount === '' ? null : parseFloat(generateForm.amount),
      });
      const created = result?.created ?? 0;
      const skipped = result?.skipped ?? 0;
      setGenerateResult(
        `Created ${created}, skipped ${skipped}${
          skipped ? ' (those due dates already existed)' : ''
        }.`
      );
      await refreshAll();
    } catch (err) {
      setFlash(readError(err, 'Failed to generate payables.'));
    } finally {
      setGenerating(false);
    }
  };

  const summaryLine = () => {
    const count = summary?.payables_count ?? items.length;
    if (!count) return 'No cycles yet.';
    const bits = [
      `${count} cycle${count === 1 ? '' : 's'}`,
      `${summary?.paid_count ?? 0} paid`,
      `${summary?.overdue_count ?? 0} overdue`,
    ];
    if (summary?.next_due_date) bits.push(`next due ${summary.next_due_date}`);
    // total_penalty is the late slice of total_charge, not an extra amount on top
    // of it, so the two are reported as a single phrase.
    if (summary?.total_charge || summary?.total_penalty) {
      const penalty = summary?.total_penalty ?? 0;
      bits.push(
        `charges ${formatMoney(summary?.total_charge ?? 0)}${
          penalty ? `, of which ${formatMoney(penalty)} incurred late` : ''
        }`
      );
    }
    if (summary?.days_late_total) bits.push(`${summary.days_late_total} days late in total`);
    return bits.join(' · ');
  };

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 1100 }}>
        <ModalHeader>
          <ModalTitle>Payables — {bill?.biller || 'Bill'}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>
        {flashMessage && <FlashMessage message={flashMessage} />}

        <p style={{ color: '#a0a0a0', fontSize: '0.85rem', marginTop: 0 }}>
          One row per due cycle. Link the payment that settled a cycle and the server
          works out how early or late it was and what the payment charged. A charge
          incurred after the due date is also shown as the penalty.
        </p>
        <p style={{ color: '#ddd', fontSize: '0.9rem', marginTop: 0 }}>{summaryLine()}</p>

        <form onSubmit={handleSubmit}>
          <h4 style={{ marginBottom: 12 }}>{editingId ? 'Edit cycle' : 'Add cycle'}</h4>
          <FormRow>
            <Label htmlFor="payable_due_date">Due date</Label>
            <BillDateInput
              id="payable_due_date"
              name="due_date"
              value={form.due_date}
              onChange={handleChange}
              required
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="payable_amount">Amount</Label>
            <BillNumberInput
              id="payable_amount"
              name="amount"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={handleChange}
            />
            <HelperText>Leave blank to use the bill's amount ({formatMoney(bill?.amount)}).</HelperText>
          </FormRow>
          <FormRow>
            <Label htmlFor="payable_remarks">Remarks (optional)</Label>
            <input
              id="payable_remarks"
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              maxLength={500}
              style={textInputStyle}
            />
          </FormRow>
          <SubmitButton type="submit" disabled={saving}>
            {saving ? 'Saving...' : editingId ? 'Update Cycle' : 'Add Cycle'}
          </SubmitButton>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              style={{ ...buttonStyle('#555'), marginLeft: 12 }}
            >
              Cancel
            </button>
          )}
        </form>

        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={() => {
              setShowGenerate((open) => !open);
              setGenerateResult('');
            }}
            style={buttonStyle('#4a7c9e')}
          >
            {showGenerate ? 'Hide monthly generator' : 'Generate monthly cycles'}
          </button>
        </div>

        {showGenerate && (
          <div style={panelStyle}>
            <form onSubmit={handleGenerate}>
              <strong>Generate a monthly series</strong>
              <p style={{ color: '#a0a0a0', fontSize: '0.85rem', marginTop: 6 }}>
                Same day each month, clamped to short months. Due dates that already
                exist are skipped, so running this again is safe.
              </p>
              <FormRow>
                <Label htmlFor="generate_start_date">Start date</Label>
                <BillDateInput
                  id="generate_start_date"
                  name="start_date"
                  value={generateForm.start_date}
                  onChange={handleGenerateChange}
                  required
                />
              </FormRow>
              <FormRow>
                <Label htmlFor="generate_months">Number of months</Label>
                <BillNumberInput
                  id="generate_months"
                  name="months"
                  min="1"
                  max="120"
                  step="1"
                  value={generateForm.months}
                  onChange={handleGenerateChange}
                  required
                />
              </FormRow>
              <FormRow>
                <Label htmlFor="generate_amount">Amount per cycle (optional)</Label>
                <BillNumberInput
                  id="generate_amount"
                  name="amount"
                  min="0"
                  step="0.01"
                  value={generateForm.amount}
                  onChange={handleGenerateChange}
                />
              </FormRow>
              <SubmitButton type="submit" disabled={generating}>
                {generating ? 'Generating...' : 'Generate'}
              </SubmitButton>
            </form>
            {generateResult && (
              <p style={{ color: '#c8e6c9', marginBottom: 0 }}>{generateResult}</p>
            )}
          </div>
        )}

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Due cycles</h4>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <BillsTableWrapper style={{ marginTop: 8 }}>
            <BillsTableEl style={{ minWidth: 1060 }}>
              <thead>
                <tr>
                  <BillsTableHeader
                    onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                    title="Sort by due date"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Due date {sortDir === 'asc' ? '▲' : '▼'}
                  </BillsTableHeader>
                  <BillsTableHeader>Amount</BillsTableHeader>
                  <BillsTableHeader>Status</BillsTableHeader>
                  <BillsTableHeader>Paid on</BillsTableHeader>
                  <BillsTableHeader>Early / Late</BillsTableHeader>
                  <BillsTableHeader>Charge</BillsTableHeader>
                  <BillsTableHeader>Penalty</BillsTableHeader>
                  <BillsTableHeader>Remarks</BillsTableHeader>
                  <BillsTableHeader>Actions</BillsTableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 16, color: '#888' }}>
                      No due cycles yet. Add one above, or generate a monthly series.
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((payable) => {
                    const chip = getPayableStatus(payable);
                    const timing = earlyLateLabel(payable);
                    const charge = Number(payable.charge) || 0;
                    const penalty = Number(payable.penalty) || 0;
                    const isLinked = Boolean(payable.payment_id);
                    const options = availablePayments(payable);
                    const rowBusy = busyId === payable.id;

                    return (
                      <BillsTableRow key={payable.id || payable.due_date}>
                        <BillsTableData>{payable.due_date || '—'}</BillsTableData>
                        <BillsTableData>
                          {formatMoney(payable.amount ?? bill?.amount)}
                        </BillsTableData>
                        <BillsTableData>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              flexWrap: 'wrap',
                            }}
                          >
                            <StatusBadgeBills $color={chip.color}>
                              {chip.label}
                            </StatusBadgeBills>
                            {payable.is_late && (
                              <span
                                style={{ color: STATUS_COLORS.overdue, fontSize: '0.8rem' }}
                              >
                                {timing.text}
                              </span>
                            )}
                          </span>
                        </BillsTableData>
                        <BillsTableData>
                          {isLinked ? (
                            <>
                              <div>{dateOnly(payable.paid_on) || '—'}</div>
                              {payable.paid_amount != null && (
                                <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>
                                  {formatMoney(payable.paid_amount)}
                                </div>
                              )}
                            </>
                          ) : (
                            '—'
                          )}
                        </BillsTableData>
                        <BillsTableData style={{ color: timing.color }}>
                          {timing.text}
                        </BillsTableData>
                        <BillsTableData>
                          {charge > 0 ? formatMoney(charge) : '—'}
                        </BillsTableData>
                        <BillsTableData
                          style={{ color: penalty > 0 ? STATUS_COLORS.overdue : undefined }}
                        >
                          {penalty > 0 ? formatMoney(penalty) : '—'}
                        </BillsTableData>
                        <BillsTableData>{payable.remarks || '—'}</BillsTableData>
                        <BillsTableData>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div>
                              {pendingDeleteId === payable.id ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(payable.id)}
                                    disabled={rowBusy}
                                    style={linkBtnStyle('#f44336')}
                                  >
                                    {rowBusy ? 'Deleting...' : 'Confirm'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setPendingDeleteId(null)}
                                    disabled={rowBusy}
                                    style={linkBtnStyle('#bbb')}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleEdit(payable)}
                                    title="Edit"
                                    style={linkBtnStyle('#4caf50')}
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPendingDeleteId(payable.id);
                                      setPendingUnlinkId(null);
                                    }}
                                    style={linkBtnStyle('#f44336')}
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>

                            {isLinked ? (
                              <div>
                                {pendingUnlinkId === payable.id ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleUnlink(payable.id)}
                                      disabled={rowBusy}
                                      style={linkBtnStyle('#f44336')}
                                    >
                                      {rowBusy ? 'Unlinking...' : 'Confirm unlink'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setPendingUnlinkId(null)}
                                      disabled={rowBusy}
                                      style={linkBtnStyle('#bbb')}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPendingUnlinkId(payable.id);
                                      setPendingDeleteId(null);
                                    }}
                                    style={linkBtnStyle('#f44336')}
                                    title="Unlink this payment (the payment itself is kept)"
                                  >
                                    Unlink payment
                                  </button>
                                )}
                              </div>
                            ) : options.length === 0 ? (
                              <span style={{ color: '#888', fontSize: '0.8rem' }}>
                                No unlinked payments
                              </span>
                            ) : (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <select
                                  value={linkChoice[payable.id] || ''}
                                  onChange={(e) =>
                                    setLinkChoice((prev) => ({
                                      ...prev,
                                      [payable.id]: e.target.value,
                                    }))
                                  }
                                  style={selectStyle}
                                  aria-label={`Select a payment for ${payable.due_date}`}
                                >
                                  <option value="">Select a payment...</option>
                                  {options.map((p) => (
                                    <option key={paymentId(p)} value={paymentId(p)}>
                                      {paymentLabel(p)}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  onClick={() => handleLink(payable.id)}
                                  disabled={rowBusy}
                                  style={linkBtnStyle('#4caf50')}
                                >
                                  {rowBusy ? 'Linking...' : 'Link'}
                                </button>
                              </div>
                            )}
                          </div>
                        </BillsTableData>
                      </BillsTableRow>
                    );
                  })
                )}
              </tbody>
            </BillsTableEl>
          </BillsTableWrapper>
        )}

        <button type="button" onClick={onClose} style={{ ...buttonStyle('#555'), marginTop: 16 }}>
          Close
        </button>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default BillPayablesModal;
